import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
    Menu,
    X,
    AlertTriangle,
    ChevronDown,
    Briefcase,
    Tag,
    Users,
    Download,
} from "lucide-react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";

const SA_CHILDREN = [
    { to: "/autodestruccion-sa", label: "Capítulo en vivo", code: "00", icon: Briefcase, blurb: "Home original, ahora capítulo 3 del libro" },
    { to: "/productos", label: "Productos", code: "01", icon: Briefcase, blurb: "Catálogo de sustancias-personajes" },
    { to: "/planes", label: "Planes", code: "02", icon: Tag, blurb: "Pricing irónico del infierno" },
    { to: "/equipo", label: "Equipo ejecutivo", code: "03", icon: Users, blurb: "Organigrama interno" },
];

const NAV_LINKS = [
    { to: "/", label: "Inicio", code: "00" },
    { to: "/libro", label: "El Libro", code: "01" },
    { to: "/mapa-inverso", label: "Mapa Inverso", code: "MI", featured: true },
    {
        type: "group",
        label: "Autodestrucción S.A.",
        code: "03",
        children: SA_CHILDREN,
    },
    { to: "/antipoesia", label: "Antipoesía", code: "04" },
    { to: "/glosario", label: "Glosario", code: "05" },
    { to: "/blog", label: "Archivo", code: "06" },
    { to: "/nelson-gonzalez-rodriguez", label: "Acerca de Nelson", code: "07" },
    { to: "/contacto", label: "Contacto", code: "08" },
];

function SADropdown({ pathname }) {
    const [open, setOpen] = useState(false);
    const childPaths = SA_CHILDREN.map((c) => c.to);
    const isActive = childPaths.includes(pathname);

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    className={`font-mono text-[11px] tracking-[0.15em] uppercase px-2.5 py-2 whitespace-nowrap inline-flex items-center gap-1.5 transition-colors hover:text-[var(--danger)] ${
                        isActive || open
                            ? "text-[var(--paper-2)] border-b border-[var(--danger)]"
                            : "text-[var(--ink-muted)]"
                    }`}
                    data-testid="nav-sa-dropdown-trigger"
                    aria-expanded={open}
                    aria-haspopup="menu"
                >
                    Autodestrucción
                    <ChevronDown
                        className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`}
                    />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                sideOffset={12}
                className="w-80 p-0 bg-[#0d0d0d] border border-[var(--danger)] rounded-none shadow-[6px_6px_0_rgba(211,47,47,0.4)]"
                data-testid="nav-sa-dropdown-popover"
            >
                <div className="border-b border-[var(--line)] bg-[var(--paper-2)] px-4 py-2 flex justify-between items-center">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-black font-bold">
                        Catálogo corporativo
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--danger)]">
                        S.A.
                    </span>
                </div>
                <ul className="p-2" role="menu">
                    {SA_CHILDREN.map((c) => {
                        const Icon = c.icon;
                        const active = pathname === c.to;
                        return (
                            <li key={c.to} role="none">
                                <Link
                                    to={c.to}
                                    role="menuitem"
                                    className={`flex items-start gap-3 px-3 py-3 transition-colors group ${active ? "bg-[var(--danger)] text-[var(--paper-2)]" : "text-[var(--paper-2)] hover:bg-[var(--danger)]"}`}
                                    data-testid={`nav-sa-child-${c.code}`}
                                >
                                    <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-mono text-[11px] uppercase tracking-[0.2em]">
                                            <span className="text-[var(--ink-dim)] group-hover:text-[var(--paper-2)] mr-1">
                                                {c.code}/
                                            </span>
                                            {c.label}
                                        </div>
                                        <p className="font-mono text-[10px] text-[var(--ink-muted)] group-hover:text-[var(--paper)] mt-0.5">
                                            {c.blurb}
                                        </p>
                                    </div>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </PopoverContent>
        </Popover>
    );
}

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            className="sticky top-0 z-50 bg-[#111] border-b border-[var(--line)]"
            data-testid="site-navbar"
        >
            {/* Top warning bar */}
            <div className="hazard-stripes-thin">
                <div className="container-x py-1.5 flex items-center justify-between">
                    <p className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-black font-bold">
                        <AlertTriangle className="inline-block w-3 h-3 mr-1.5 -mt-0.5" />
                        El libro de Nelson González · Lectura responsable
                    </p>
                    <p className="hidden md:block font-mono text-[10px] uppercase tracking-[0.25em] text-black font-bold">
                        Descarga 100% gratis · sin costo · sin condiciones
                    </p>
                </div>
            </div>

            {/* Main navbar */}
            <div className="container-x flex items-center justify-between py-4 gap-4">
                <Link
                    to="/"
                    className="group flex items-center gap-3 flex-shrink-0"
                    data-testid="nav-logo-link"
                >
                    <span className="font-stamp text-[var(--danger)] border-2 border-[var(--danger)] px-2 py-0.5 text-[10px] tracking-[0.2em] uppercase rotate-[-3deg] inline-block">
                        EMI
                    </span>
                    <span className="flex flex-col leading-none">
                        <span className="font-display text-lg md:text-xl tracking-tight text-[var(--paper-2)]">
                            El Mapa <span className="text-[var(--danger)]">Inverso</span>
                        </span>
                        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[var(--ink-muted)] mt-0.5">
                            por Nelson González
                        </span>
                    </span>
                </Link>

                <nav
                    className="hidden 2xl:flex items-center gap-1"
                    aria-label="Navegación principal"
                >
                    {NAV_LINKS.map((l) => {
                        if (l.type === "group") {
                            return (
                                <SADropdown
                                    key="sa-group"
                                    pathname={location.pathname}
                                />
                            );
                        }
                        return (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`nav-link-${l.code}`}
                                className={({ isActive }) =>
                                    l.featured
                                        ? `font-mono text-[11px] tracking-[0.15em] uppercase px-2.5 py-2 whitespace-nowrap transition-colors inline-flex items-center gap-1 border border-[var(--warning)] ${
                                              isActive
                                                  ? "bg-[var(--warning)] text-black"
                                                  : "text-[var(--warning)] hover:bg-[var(--warning)] hover:text-black"
                                          }`
                                        : `font-mono text-[11px] tracking-[0.15em] uppercase px-2.5 py-2 whitespace-nowrap transition-colors hover:text-[var(--danger)] ${
                                              isActive
                                                  ? "text-[var(--paper-2)] border-b border-[var(--danger)]"
                                                  : "text-[var(--ink-muted)]"
                                          }`
                                }
                            >
                                {!l.featured && (
                                    <span className="text-[var(--ink-dim)] mr-1">
                                        {l.code}/
                                    </span>
                                )}
                                {l.label}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="flex items-center gap-3 flex-shrink-0">
                    <Link
                        to="/descargar"
                        className="hidden md:inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2 bg-[var(--danger)] text-[var(--paper-2)] hover:bg-[var(--paper-2)] hover:text-black transition-colors"
                        data-testid="nav-comprar-cta"
                    >
                        <Download className="w-3.5 h-3.5" />
                        Descargar gratis
                    </Link>
                    <button
                        onClick={() => setOpen(!open)}
                        className="2xl:hidden p-2 text-[var(--paper-2)]"
                        aria-label="Abrir menú"
                        data-testid="nav-mobile-toggle"
                    >
                        {open ? <X /> : <Menu />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    className="2xl:hidden border-t border-[var(--line)] bg-[#0d0d0d]"
                    data-testid="nav-mobile-menu"
                >
                    <nav
                        className="container-x py-6 flex flex-col gap-1"
                        aria-label="Navegación móvil"
                    >
                        {NAV_LINKS.map((l) => {
                            if (l.type === "group") {
                                return (
                                    <div
                                        key="mobile-sa-group"
                                        className="py-3 border-b border-[var(--line)]"
                                        data-testid="nav-mobile-sa-group"
                                    >
                                        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--danger)] mb-3">
                                            <span className="text-[var(--ink-dim)] mr-2">
                                                {l.code}/
                                            </span>
                                            {l.label}
                                        </p>
                                        <div className="space-y-2 pl-4 border-l-2 border-dashed border-[var(--line)]">
                                            {l.children.map((c) => (
                                                <NavLink
                                                    key={c.to}
                                                    to={c.to}
                                                    data-testid={`nav-mobile-sa-child-${c.code}`}
                                                    className={({ isActive }) =>
                                                        `block font-mono text-sm tracking-[0.15em] uppercase py-1.5 ${isActive ? "text-[var(--danger)]" : "text-[var(--paper-2)]"}`
                                                    }
                                                >
                                                    <span className="text-[var(--ink-dim)] mr-2">
                                                        {c.code}/
                                                    </span>
                                                    {c.label}
                                                </NavLink>
                                            ))}
                                        </div>
                                    </div>
                                );
                            }
                            return (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    end={l.to === "/"}
                                    data-testid={`nav-mobile-link-${l.code}`}
                                    className={({ isActive }) =>
                                        l.featured
                                            ? `font-mono text-sm tracking-[0.15em] uppercase py-3 border-b border-[var(--line)] ${
                                                  isActive
                                                      ? "text-black bg-[var(--warning)] px-3"
                                                      : "text-[var(--warning)]"
                                              }`
                                            : `font-mono text-sm tracking-[0.15em] uppercase py-3 border-b border-[var(--line)] ${isActive ? "text-[var(--danger)]" : "text-[var(--paper-2)]"}`
                                    }
                                >
                                    <span className="text-[var(--ink-dim)] mr-2">
                                        {l.code}/
                                    </span>
                                    {l.label}
                                </NavLink>
                            );
                        })}
                    </nav>
                </div>
            )}
        </header>
    );
}
